import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, AlertCircle, FileText, CheckCircle2, Download } from "lucide-react";
import { toast } from "sonner";

const eur = (n) =>
  (n ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const date = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");

// Etichetta tipo doc
const TIPO_LABEL = { D: "DDT", F: "Fattura", N: "NC", I: "Insoluto" };

export default function ClientStatement() {
  const { authAxios } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get("/client/me/estratto-conto");
      setData(r.data);
    } finally { setLoading(false); }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await authAxios.get("/client/me/estratto-conto/pdf", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Estrae il filename dall'header Content-Disposition se possibile
      const cd = r.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      a.download = match ? match[1] : "EstrattoConto.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF scaricato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF non disponibile");
    } finally { setDownloading(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-zinc-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento estratto...
      </div>
    );
  }

  if (!data || data.n_fatture === 0) {
    return (
      <Card className="bg-emerald-500/5 border-emerald-500/20 p-8 text-center" data-testid="statement-empty">
        <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
        <p className="text-zinc-100 font-semibold">Nessuna fattura aperta</p>
        <p className="text-zinc-400 text-sm mt-1">Tutto in regola. Grazie!</p>
      </Card>
    );
  }

  const today = new Date();
  const isOverdue = (iso) => {
    if (!iso) return false;
    const d = new Date(iso);
    return d < today;
  };

  return (
    <div className="space-y-4" data-testid="client-statement">
      {/* Download button + Summary cards */}
      <div className="flex justify-end">
        <Button
          onClick={handleDownloadPdf}
          disabled={downloading}
          data-testid="download-statement-pdf"
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          {downloading ? (
            <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generazione PDF...</>
          ) : (
            <><Download className="w-4 h-4 mr-1.5" /> Scarica PDF</>
          )}
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
        <Card className="bg-zinc-900 border-zinc-800 p-3 sm:p-4">
          <p className="text-[10px] sm:text-xs text-zinc-500 uppercase mb-1">N° Fatture</p>
          <p className="text-xl sm:text-2xl font-bold text-zinc-100">{data.n_fatture}</p>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 p-3 sm:p-4">
          <p className="text-[10px] sm:text-xs text-zinc-500 uppercase mb-1">Importo</p>
          <p className="text-xl sm:text-2xl font-bold text-zinc-100">{eur(data.totale_importo)}</p>
        </Card>
        <Card className="bg-red-500/10 border-red-500/30 p-3 sm:p-4 col-span-2 sm:col-span-1">
          <p className="text-[10px] sm:text-xs text-red-300 uppercase mb-1">Saldo da pagare</p>
          <p className="text-xl sm:text-2xl font-bold text-red-300">{eur(data.totale_saldo)}</p>
        </Card>
      </div>

      {/* Invoices table */}
      <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-zinc-950/50 text-zinc-400 uppercase text-[10px] sm:text-xs">
              <tr>
                <th className="text-left px-3 py-2.5">Numero</th>
                <th className="text-left px-3 py-2.5">Tipo</th>
                <th className="text-left px-3 py-2.5">Data</th>
                <th className="text-left px-3 py-2.5">Scadenza</th>
                <th className="text-right px-3 py-2.5">Importo</th>
                <th className="text-right px-3 py-2.5">Saldo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {data.invoices.map((inv) => {
                const overdue = isOverdue(inv.scadenza);
                return (
                  <tr key={inv.numero} data-testid={`statement-row-${inv.numero}`} className={overdue ? "bg-red-500/5" : ""}>
                    <td className="px-3 py-2 font-mono text-zinc-100">{inv.numero}</td>
                    <td className="px-3 py-2">
                      <span className="text-zinc-400">{TIPO_LABEL[inv.tipo] || inv.tipo}</span>
                    </td>
                    <td className="px-3 py-2 text-zinc-400">{date(inv.data_doc)}</td>
                    <td className={`px-3 py-2 ${overdue ? "text-red-300 font-semibold" : "text-zinc-400"}`}>
                      {date(inv.scadenza)}
                      {overdue && <AlertCircle className="inline-block w-3 h-3 ml-1" />}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-300">{eur(inv.importo)}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-zinc-100">{eur(inv.saldo)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-[11px] text-zinc-500 text-center flex items-center justify-center gap-1.5">
        <FileText className="w-3 h-3" />
        Per qualsiasi chiarimento sulle fatture, contatta il tuo agente o l&apos;amministrazione.
      </p>
    </div>
  );
}
